import React, { useContext, createContext } from 'react'

// session related hooks

const SessionContext = createContext()

export const SessionProvider = ({ session_id, ...props }) => {
	return <SessionContext.Provider {...props} value={session_id} />
}

export const useSession = () => useContext(SessionContext)

/**
 * Zooms the whole page down so a fixed-px layout fits the window. `designWidth` x `designHeight`
 * is the smallest canvas (CSS px) the layout fits in; never zooms up. Exposes the factor as the
 * `--dash-scale` CSS var: CSS zoom also scales vh/vw lengths, so full-viewport sizes must divide
 * by it, e.g. calc(100vh / var(--dash-scale, 1)).
 *
 * Why: Tesla 2026.26 raised the in-car browser zoom (devicePixelRatio 1.0 -> ~1.53), so a
 * full-screen browser went from 1920x1160 to ~1255x758 CSS px, and less again in a windowed one.
 * @param {number} designWidth
 * @param {number} designHeight
 */
export const useFitToViewport = (designWidth, designHeight) => {
	React.useLayoutEffect(() => {
		const root = document.documentElement
		const { body } = document
		const apply = () => {
			const scale = Math.min(1, window.innerWidth / designWidth, window.innerHeight / designHeight)
			root.style.setProperty('--dash-scale', scale)
			root.style.zoom = scale
			// applicationv4.scss caps html and body at 100vw, which the zoom would shrink too
			root.style.maxWidth = body.style.maxWidth = 'calc(100vw / var(--dash-scale))'
		}
		apply()
		window.addEventListener('resize', apply)
		return () => {
			window.removeEventListener('resize', apply)
			root.style.zoom = root.style.maxWidth = body.style.maxWidth = ''
			root.style.removeProperty('--dash-scale')
		}
	}, [designWidth, designHeight])
}

/**
 * 
 * @param {string[]} activePaths 
 * @returns 
 */
export const useIsActive = (activePaths = []) => {
	const { pathname } = window.location
    const isActive = React.useMemo(() => activePaths.map(path => new RegExp(path).test(pathname)).includes(true), [activePaths, pathname])
	return isActive
}
